import { BRAND } from "themes";

const motion = {
  "@media (prefers-reduced-motion: reduce)": {
    transition: "none",
  },
};

export const styles = {
  page: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
  },
  profile: {
    display: "flex",
    alignItems: { xs: "flex-start", sm: "center" },
    gap: "18px",
    padding: { xs: "18px", md: "22px" },
    backgroundColor: "#fff",
    border: `1px solid ${BRAND.border}`,
    borderRadius: "22px",
    boxShadow: "0 10px 30px -18px rgba(27, 19, 64, 0.28)",
  },
  photo: {
    width: 72,
    height: 72,
    borderRadius: "50%",
    objectFit: "cover" as const,
    flexShrink: 0,
    boxShadow: "0 0 0 4px #EDE9FE",
  },
  initials: {
    width: 72,
    height: 72,
    borderRadius: "50%",
    display: "grid",
    placeItems: "center",
    flexShrink: 0,
    color: "#fff",
    backgroundColor: BRAND.primary,
    fontWeight: 700,
    fontSize: 24,
    lineHeight: 1,
    letterSpacing: "-0.02em",
    boxShadow: "0 0 0 4px #EDE9FE",
  },
  name: {
    color: BRAND.ink,
    fontWeight: 700,
    fontSize: { xs: 22, md: 26 },
    lineHeight: 1.15,
    letterSpacing: "-0.03em",
    textTransform: "capitalize" as const,
  },
  role: {
    display: "inline-flex",
    alignItems: "center",
    marginTop: "8px",
    padding: "4px 10px",
    borderRadius: "999px",
    backgroundColor: "#EDE9FE",
    color: BRAND.primary,
    fontWeight: 600,
    fontSize: 12,
    lineHeight: "16px",
  },
  address: {
    display: "flex",
    alignItems: "flex-start",
    gap: "6px",
    marginTop: "10px",
    color: "#5C5870",
    fontWeight: 500,
    fontSize: 14,
    lineHeight: "20px",
    "& .MuiSvgIcon-root": {
      fontSize: 18,
      marginTop: "1px",
      color: BRAND.primary,
    },
  },
  section: {
    backgroundColor: "#fff",
    border: `1px solid ${BRAND.border}`,
    borderRadius: "22px",
    padding: { xs: "16px", md: "22px" },
    boxShadow: "0 10px 30px -18px rgba(27, 19, 64, 0.22)",
  },
  sectionHead: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "12px",
    flexWrap: "wrap",
    marginBottom: "16px",
  },
  sectionTitle: {
    color: BRAND.ink,
    fontWeight: 700,
    fontSize: 16,
    lineHeight: "22px",
    letterSpacing: "-0.02em",
  },
  count: {
    display: "inline-flex",
    alignItems: "center",
    marginLeft: "10px",
    padding: "3px 8px",
    borderRadius: "999px",
    backgroundColor: "#EDE9FE",
    color: BRAND.primary,
    fontWeight: 600,
    fontSize: 12,
    lineHeight: "16px",
    letterSpacing: 0,
    verticalAlign: "middle",
  },
  grid: {
    display: "grid",
    gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", lg: "1fr 1fr 1fr" },
    gap: "12px",
  },
  tile: {
    display: "flex",
    alignItems: "flex-start",
    gap: "12px",
    minWidth: 0,
    margin: 0,
    padding: "14px",
    borderRadius: "16px",
    border: "1px solid transparent",
    backgroundColor: BRAND.track,
    color: "inherit",
    font: "inherit",
    textAlign: "left" as const,
  },
  tileButton: {
    display: "flex",
    alignItems: "flex-start",
    gap: "12px",
    width: "100%",
    minWidth: 0,
    margin: 0,
    padding: "14px",
    borderRadius: "16px",
    border: "1px solid transparent",
    backgroundColor: BRAND.track,
    color: "inherit",
    font: "inherit",
    textAlign: "left" as const,
    cursor: "pointer",
    transition: "background-color 200ms ease, border-color 200ms ease",
    ...motion,
    "&:hover": {
      backgroundColor: "#F7F5FF",
      borderColor: "#DDD8EE",
    },
    "&:focus-visible": {
      outline: `2px solid ${BRAND.primary}`,
      outlineOffset: 2,
    },
  },
  tileIcon: {
    width: 36,
    height: 36,
    borderRadius: "12px",
    display: "grid",
    placeItems: "center",
    flexShrink: 0,
    color: BRAND.primary,
    backgroundColor: "#fff",
    "& .MuiSvgIcon-root": { fontSize: 20 },
  },
  tileLabel: {
    color: "#5C5870",
    fontWeight: 600,
    fontSize: 12,
    lineHeight: "16px",
    letterSpacing: "0.04em",
    textTransform: "uppercase" as const,
  },
  tileValue: {
    display: "flex",
    alignItems: "center",
    gap: "4px",
    marginTop: "4px",
    color: BRAND.ink,
    fontWeight: 600,
    fontSize: 15,
    lineHeight: "22px",
    wordBreak: "break-word" as const,
    "& .MuiSvgIcon-root": { fontSize: 16, color: BRAND.primary },
  },
  dateButton: {
    height: 36,
    borderColor: "transparent",
    color: BRAND.ink,
    backgroundColor: "#fff",
    textTransform: "none" as const,
    fontWeight: 600,
    fontSize: 13,
    borderRadius: "999px",
    padding: "4px 12px",
    gap: "8px",
    border: `1px solid ${BRAND.border}`,
    transition: "background-color 200ms ease",
    ...motion,
    "&:hover": {
      borderColor: BRAND.border,
      backgroundColor: "#F7F5FF",
    },
    "&:focus-visible": {
      outline: `2px solid ${BRAND.primary}`,
      outlineOffset: 2,
    },
  },
  dialogPaper: {
    width: "calc(100% - 32px)",
    maxWidth: 720,
    margin: "16px",
    borderRadius: "22px",
    border: `1px solid ${BRAND.border}`,
    boxShadow: "0 24px 50px -20px rgba(27, 19, 64, 0.35)",
  },
  dialogHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "12px",
    padding: "18px 18px 8px",
  },
  dialogTitle: {
    color: BRAND.ink,
    fontWeight: 700,
    fontSize: 18,
    lineHeight: "24px",
    letterSpacing: "-0.02em",
  },
  dialogClose: {
    width: 36,
    height: 36,
    borderRadius: "12px",
    border: `1px solid ${BRAND.border}`,
    color: BRAND.ink,
    "&:hover": { backgroundColor: "#F7F5FF" },
    "&:focus-visible": {
      outline: `2px solid ${BRAND.primary}`,
      outlineOffset: 2,
    },
  },
  dialogBody: {
    padding: "4px 8px 0",
    "& .MuiPaper-root": {
      boxShadow: "none",
    },
  },
  dialogFooter: {
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    gap: "8px",
    padding: "8px 18px 18px",
  },
  cancelButton: {
    minWidth: 0,
    height: 40,
    padding: "6px 14px",
    borderRadius: "12px",
    color: "#5C5870",
    fontWeight: 600,
    fontSize: 14,
    textTransform: "none" as const,
    "&:hover": { backgroundColor: BRAND.track },
  },
  applyButton: {
    height: 40,
    padding: "6px 16px",
    borderRadius: "12px",
    boxShadow: "none",
    textTransform: "none" as const,
    fontWeight: 600,
    fontSize: 14,
    backgroundColor: BRAND.primary,
    "&:hover": {
      boxShadow: "none",
      backgroundColor: BRAND.primaryDark,
    },
  },
  tableWrap: {
    border: `1px solid ${BRAND.border}`,
    borderRadius: "16px",
    overflow: "auto",
  },
  table: {
    minWidth: 720,
  },
  tableHead: {
    backgroundColor: "#F7F6FB",
    "& th": {
      padding: "14px 16px",
      fontWeight: 600,
      fontSize: "0.8125rem",
      lineHeight: "18px",
      letterSpacing: "0.04em",
      textTransform: "uppercase",
      color: "#5C5870",
      whiteSpace: "nowrap",
      borderBottom: `1px solid ${BRAND.border}`,
      backgroundColor: "#F7F6FB",
    },
  },
  tableRow: {
    transition: "background-color 160ms ease",
    ...motion,
    "&:hover": { backgroundColor: "#F9F8FD" },
    "& td": {
      padding: "14px 16px",
      fontWeight: 500,
      fontSize: 14,
      lineHeight: "20px",
      color: "#364152",
      borderBottom: `1px solid ${BRAND.border}`,
      whiteSpace: "nowrap",
    },
    "&:last-of-type td": { borderBottom: "none" },
  },
  nameButton: {
    display: "inline-flex",
    alignItems: "center",
    gap: "10px",
    margin: 0,
    padding: 0,
    border: 0,
    background: "transparent",
    color: BRAND.ink,
    font: "inherit",
    fontWeight: 600,
    fontSize: 14,
    lineHeight: "20px",
    cursor: "pointer",
    textAlign: "left" as const,
    textTransform: "capitalize" as const,
    "&:hover": { color: BRAND.primary },
    "&:focus-visible": {
      outline: `2px solid ${BRAND.primary}`,
      outlineOffset: 2,
      borderRadius: "8px",
    },
  },
  rowAvatar: {
    width: 32,
    height: 32,
    borderRadius: "50%",
    display: "grid",
    placeItems: "center",
    flexShrink: 0,
    color: "#fff",
    backgroundColor: BRAND.primary,
    fontWeight: 700,
    fontSize: 12,
    lineHeight: 1,
    textTransform: "uppercase" as const,
  },
  empty: {
    color: BRAND.muted,
    fontWeight: 500,
    fontSize: 14,
    lineHeight: "20px",
    textAlign: "center" as const,
    padding: "28px 12px",
  },
  stateCard: {
    backgroundColor: "#fff",
    border: `1px solid ${BRAND.border}`,
    borderRadius: "22px",
    padding: "40px 20px",
    textAlign: "center" as const,
  },
  stateTitle: {
    color: BRAND.ink,
    fontWeight: 700,
    fontSize: 18,
    lineHeight: "24px",
    letterSpacing: "-0.02em",
  },
};
